export type TraceSpan = {
  traceId: string;
  spanId: string;
  parentSpanId?: string | null;
  correlationId?: string | null;
  pipelineStage?: string | null;
  tenantId?: string | null;
  notebookId?: string | null;
  fileId?: string | null;
  threadId?: string | null;
  apiName?: string | null;
  engine?: string | null;
  tip?: string | null;
  startedAt: number;
  endedAt?: number;
  durationMs?: number;
  status?: 'running' | 'ok' | 'error';
  error?: string | null;
};

type Listener = () => void;

const state = {
  spans: [] as TraceSpan[],
  listeners: new Set<Listener>(),
};

const notify = () => {
  state.listeners.forEach(cb => cb());
};

const genId = (prefix = '') => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `${prefix}${(crypto as any).randomUUID()}`;
    }
  } catch {}
  return `${prefix}${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
};

export const traceStore = {
  subscribe(cb: Listener) {
    state.listeners.add(cb);
    return () => state.listeners.delete(cb);
  },
  getSnapshot() {
    return state.spans;
  },
  addSpan(span: TraceSpan) {
    state.spans.push(span);
    notify();
  },
  upsertSpan(span: TraceSpan) {
    const idx = state.spans.findIndex(s => s.spanId === span.spanId);
    if (idx >= 0) {
      state.spans[idx] = { ...state.spans[idx], ...span };
    } else {
      state.spans.push(span);
    }
    notify();
  },
  updateSpan(spanId: string, patch: Partial<TraceSpan>) {
    const idx = state.spans.findIndex(s => s.spanId === spanId);
    if (idx >= 0) {
      state.spans[idx] = { ...state.spans[idx], ...patch };
      notify();
    }
  },
  clear() {
    state.spans = [];
    notify();
  },
};

export const createTraceSpan = (meta: Partial<TraceSpan> = {}): TraceSpan => {
  const traceId = meta.traceId || genId('t_');
  const spanId = meta.spanId || genId('s_');
  const correlationId = meta.correlationId || genId('c_');
  const span: TraceSpan = {
    traceId,
    spanId,
    correlationId,
    parentSpanId: meta.parentSpanId || null,
    pipelineStage: meta.pipelineStage || null,
    tenantId: meta.tenantId || null,
    notebookId: meta.notebookId || null,
    fileId: meta.fileId || null,
    threadId: meta.threadId || null,
    apiName: meta.apiName || null,
    engine: meta.engine || null,
    tip: meta.tip || null,
    startedAt: Date.now(),
    status: 'running',
  };
  traceStore.addSpan(span);
  return span;
};

export const endTraceSpan = (spanId: string, patch: Partial<TraceSpan> = {}) => {
  const span = traceStore.getSnapshot().find(s => s.spanId === spanId);
  const endedAt = Date.now();
  const durationMs = span?.startedAt ? endedAt - span.startedAt : undefined;
  traceStore.updateSpan(spanId, {
    endedAt,
    durationMs,
    status: patch.status || 'ok',
    error: patch.error || null,
    ...patch,
  });
};

export const createChildSpan = (parent: TraceSpan, meta: Partial<TraceSpan> = {}) => {
  return createTraceSpan({
    ...meta,
    traceId: meta.traceId || parent.traceId,
    parentSpanId: parent.spanId,
    correlationId: meta.correlationId || parent.correlationId || null,
  });
};

export const getTraceHeaders = (span: TraceSpan) => ({
  'x-trace-id': span.traceId,
  'x-span-id': span.spanId,
  'x-correlation-id': span.correlationId || '',
});

export const getTraceQuery = (span: TraceSpan) => ({
  traceId: span.traceId,
  spanId: span.spanId,
});

export const recordRemoteSpan = (payload: Partial<TraceSpan> & { traceId?: string; spanId?: string }) => {
  if (!payload?.traceId || !payload?.spanId) return;
  const existing = traceStore.getSnapshot().find(s => s.spanId === payload.spanId);
  const base: TraceSpan =
    existing ||
    ({
      traceId: payload.traceId,
      spanId: payload.spanId,
      startedAt: Date.now(),
      status: 'running',
    } as TraceSpan);
  traceStore.upsertSpan({
    ...base,
    ...payload,
  } as TraceSpan);
};

export const traceFetch = async (
  input: RequestInfo,
  init: RequestInit = {},
  meta: Partial<TraceSpan> = {}
) => {
  const url =
    typeof input === 'string'
      ? input
      : (input as Request)?.url || meta.apiName || 'fetch';
  const method = (init.method || (input as Request)?.method || 'GET').toString();
  const span = createTraceSpan({
    apiName: url,
    pipelineStage: meta.pipelineStage || 'http',
    tip: method,
    ...meta,
  });

  const headers = new Headers(
    init.headers || (input instanceof Request ? input.headers : undefined)
  );
  const traceHeaders = getTraceHeaders(span);
  Object.entries(traceHeaders).forEach(([k, v]) => headers.set(k, v));

  try {
    const request =
      input instanceof Request ? new Request(input, { ...init, headers }) : input;
    const res = await fetch(request, { ...init, headers });
    endTraceSpan(span.spanId, {
      status: res.ok ? 'ok' : 'error',
      apiName: url,
    });
    return res;
  } catch (err: any) {
    endTraceSpan(span.spanId, {
      status: 'error',
      apiName: url,
      error: err?.message || String(err),
    });
    throw err;
  }
};
